import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Alert,
  CircularProgress,
  Stack,
} from '@mui/material';

import api from '../../api/axiosConfig';
import { useInstatDesign } from './theme/instatDesign';
import {
  Card,
  SegmentedControl,
  Pill,
  Skeleton,
} from './ui';
import PlayerHeroCard from './PlayerHeroCard';
import OverviewDashboard from './OverviewDashboard';
import GamesTab from './GamesTab';
import CareerTab from './CareerTab';
import ShotChart from './ShotChart';
import InstatExtensionBanner from './InstatExtensionBanner';
import { primeExtensionCredentials, primeExtensionAppAuth } from './instatExtension';
import ClipsTab from './ClipsTab';
import InformesTab from './InformesTab';

// Reusable Instat player view. Pass `instatPlayerId` (numeric Hudl id) and the
// component handles overview / games / career / shooting / clips for that
// player. `localPlayerId` is optional — when provided (e.g. embedded inside
// PlayerProfile), saved clips are filtered to this local player and the
// extension is primed with the {localPlayerId, instatPlayerId} context so the
// Save-to-Valencia button on Hudl tags each clip with both ids.
//
// When `embedded` is true, the component skips the page-level chrome (sticky
// header, banner) — the host page is expected to provide that.
export default function InstatPlayerView({
  instatPlayerId,
  localPlayerId = null,
  embedded = false,
  stickyTop = 72,
}) {
  const d = useInstatDesign();

  const [playerId, setPlayerId] = useState(instatPlayerId || null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [overview, setOverview] = useState(null);
  const [games, setGames] = useState(null);
  const [seasonsCatalog, setSeasonsCatalog] = useState([]);
  const [seasonId, setSeasonId] = useState(null);
  const [gamesLoading, setGamesLoading] = useState(false);

  const [statsData, setStatsData] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);

  const [shotsData, setShotsData] = useState(null);
  const [shotsLoading, setShotsLoading] = useState(false);

  const [activeTab, setActiveTab] = useState('overview');
  const [clipsCount, setClipsCount] = useState(null);
  const [informesCount, setInformesCount] = useState(null);

  // Keep internal id in sync with prop (PlayerProfile may switch players)
  useEffect(() => {
    setPlayerId(instatPlayerId || null);
    setOverview(null);
    setGames(null);
    setSeasonsCatalog([]);
    setSeasonId(null);
    setStatsData(null);
    setShotsData(null);
    setError(null);
    setActiveTab('overview');
  }, [instatPlayerId]);

  const playerName = overview?.info?.name;
  const matchIds = useMemo(() => (games?.matches || []).map((m) => m.id), [games]);
  const currSeasonLabel = useMemo(() => {
    const s = seasonsCatalog.find((x) => x.id === seasonId);
    return s?.name || '';
  }, [seasonsCatalog, seasonId]);

  const seasonOptions = useMemo(
    () => (seasonsCatalog || []).slice(0, 8).map((s) => ({ value: s.id, label: s.name })),
    [seasonsCatalog],
  );

  const playerContext = useMemo(
    () => ({ localPlayerId: localPlayerId || null, instatPlayerId: playerId || null }),
    [localPlayerId, playerId],
  );

  // Initial fetch on mount / id change
  const fetchAll = useCallback(async (id) => {
    if (!id) return;
    setError(null);
    setOverview(null);
    setGames(null);
    setSeasonsCatalog([]);
    setSeasonId(null);
    setStatsData(null);
    setShotsData(null);
    setLoading(true);
    try {
      const [{ data: ov }, { data: gm }] = await Promise.all([
        api.get(`/api/instat/player/${id}/overview`),
        api.get(`/api/instat/player/${id}/games`),
      ]);
      setOverview(ov);
      setGames(gm);
      setSeasonsCatalog(gm.seasons || []);
      setSeasonId(gm.seasons?.[0]?.id || null);
    } catch (e) {
      const detail = e.response?.data?.error || e.response?.data || e.message;
      setError(typeof detail === 'string' ? detail : JSON.stringify(detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (playerId) fetchAll(playerId);
  }, [playerId, fetchAll]);

  // Re-fetch games on season change
  useEffect(() => {
    if (!playerId || !seasonId) return;
    if (games && games.matches?.[0]?.season_id === seasonId) return;

    let aborted = false;
    setGamesLoading(true);
    api.get(`/api/instat/player/${playerId}/games?seasonId=${seasonId}`)
      .then(({ data }) => { if (!aborted) setGames(data); })
      .catch((e) => {
        if (!aborted) {
          const detail = e.response?.data?.error || e.response?.data || e.message;
          setError(typeof detail === 'string' ? detail : JSON.stringify(detail));
        }
      })
      .finally(() => { if (!aborted) setGamesLoading(false); });
    return () => { aborted = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seasonId, playerId]);

  // Season stats for hero KPI + Overview
  useEffect(() => {
    if (!playerId) return;
    let aborted = false;
    setStatsLoading(true);
    setStatsData(null);
    const url = `/api/instat/player/${playerId}/stats${seasonId ? `?seasonId=${seasonId}` : ''}`;
    api.get(url)
      .then(({ data }) => { if (!aborted) setStatsData(data); })
      .catch(() => { if (!aborted) setStatsData(null); })
      .finally(() => { if (!aborted) setStatsLoading(false); });
    return () => { aborted = true; };
  }, [playerId, seasonId]);

  // Prime Hudl creds + Valencia app auth on the extension — required for both
  // standalone /instat-poc and embedded usage in PlayerProfile (otherwise the
  // Save-clip button on Hudl can't POST to our backend with auth).
  useEffect(() => {
    primeExtensionCredentials().catch(() => null);
    primeExtensionAppAuth().catch(() => null);
    const onReprime = () => { primeExtensionAppAuth().catch(() => null); };
    document.addEventListener('instat-helper-reprime', onReprime);
    return () => document.removeEventListener('instat-helper-reprime', onReprime);
  }, []);

  // Reset shots when season changes so we re-fetch against the new match set
  useEffect(() => {
    setShotsData(null);
  }, [seasonId]);

  // Shots lazy on tab open — scoped to matches of the selected season
  useEffect(() => {
    if (activeTab !== 'shooting' || !playerId) return;
    if (shotsData || shotsLoading) return;
    if (!games || gamesLoading) return;
    const ids = (games.matches || []).map((m) => m.id);
    if (!ids.length) {
      setShotsData({ shots: [], stats: {}, matchIds: [] });
      return;
    }
    setShotsLoading(true);
    api.get(`/api/instat/player/${playerId}/shots?matchIds=${ids.join(',')}`)
      .then(({ data }) => setShotsData(data))
      .catch(() => setShotsData(null))
      .finally(() => setShotsLoading(false));
  }, [activeTab, playerId, shotsData, shotsLoading, games, gamesLoading]);

  const tabs = [
    { value: 'overview', label: 'Overview' },
    { value: 'clips',    label: 'Clips',    count: clipsCount || undefined },
    ...(localPlayerId
      ? [{ value: 'informes', label: 'Informes', count: informesCount || undefined }]
      : []),
    { value: 'games',    label: 'Games',    count: matchIds.length || undefined },
    { value: 'career',   label: 'Career' },
    { value: 'shooting', label: 'Shooting chart' },
  ];

  if (!playerId) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="info" sx={{ borderRadius: `${d.radius.md}px`, border: 'none' }}>
          Aucun Instat player id défini.
        </Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        bgcolor: embedded ? 'transparent' : d.colors.canvas,
        fontFamily: d.typo.fontFamily,
        color: d.colors.text,
        pb: embedded ? 0 : 6,
      }}
    >
      <InstatExtensionBanner />

      {error && (
        <Alert
          severity="error"
          sx={{
            mb: 3,
            whiteSpace: 'pre-wrap',
            borderRadius: `${d.radius.md}px`,
            border: 'none',
          }}
        >
          {error}
        </Alert>
      )}

      {loading && !overview && (
        <Card sx={{ mb: 3 }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Skeleton variant="rect" width={140} height={140} radius={20} />
            <Box sx={{ flex: 1 }}>
              <Skeleton height={32} width="60%" sx={{ mb: 1.5 }} />
              <Skeleton height={16} width="40%" sx={{ mb: 2 }} />
              <Stack direction="row" spacing={1}>
                {[0,1,2,3].map((i) => <Skeleton key={i} height={22} width={70} variant="pill" />)}
              </Stack>
            </Box>
          </Stack>
        </Card>
      )}

      {overview && (
        <>
          <PlayerHeroCard
            info={overview.info}
            playerId={overview.playerId}
            currStats={statsData?.currSeason?.stats}
            prevStats={statsData?.prevSeason?.stats}
            sx={{ mb: 2.5 }}
          />

          <Card
            padding={1.25}
            sx={{
              mb: 2.5,
              position: 'sticky',
              top: stickyTop,
              zIndex: 5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 1,
            }}
            tone="glass"
          >
            <SegmentedControl options={tabs} value={activeTab} onChange={setActiveTab} />
            <Stack direction="row" spacing={1} alignItems="center">
              {(gamesLoading || statsLoading) && (
                <CircularProgress size={14} sx={{ color: d.colors.textSecondary }} />
              )}
              {seasonOptions.length > 0 && (
                <SegmentedControl
                  options={seasonOptions}
                  value={seasonId}
                  onChange={setSeasonId}
                  size="sm"
                />
              )}
            </Stack>
          </Card>

          <Box hidden={activeTab !== 'overview'}>
            <OverviewDashboard
              playerId={overview.playerId}
              playerName={playerName}
              skills={overview.skills}
              currStats={statsData?.currSeason?.stats}
              seasonLabel={currSeasonLabel}
              matchIds={matchIds}
              playerContext={playerContext}
            />
          </Box>

          <Box hidden={activeTab !== 'games'}>
            <GamesTab
              playerId={overview.playerId}
              playerName={playerName}
              games={games}
              playerContext={playerContext}
            />
          </Box>

          <Box hidden={activeTab !== 'career'}>
            <CareerTab
              playerId={overview.playerId}
              playerName={playerName}
              playerContext={playerContext}
            />
          </Box>

          <Box hidden={activeTab !== 'shooting'}>
            {shotsLoading && !shotsData && (
              <Card>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <CircularProgress size={18} sx={{ color: d.colors.textSecondary }} />
                  <Typography sx={{ ...d.typo.caption }}>
                    Loading shot chart…
                  </Typography>
                </Stack>
              </Card>
            )}
            {shotsData && (
              <ShotChart
                shots={shotsData.shots}
                stats={{ ...shotsData.stats, matchCount: shotsData.matchIds?.length }}
              />
            )}
          </Box>

          <Box hidden={activeTab !== 'clips'}>
            <ClipsTab
              localPlayerId={localPlayerId}
              instatPlayerId={playerId}
              onCountChange={setClipsCount}
            />
          </Box>

          {localPlayerId && (
            <Box hidden={activeTab !== 'informes'}>
              <InformesTab
                localPlayerId={localPlayerId}
                instatPlayerId={playerId}
                playerName={playerName}
                onCountChange={setInformesCount}
              />
            </Box>
          )}

          <Stack direction="row" spacing={1} sx={{ mt: 3, flexWrap: 'wrap', rowGap: 0.75 }}>
            <Pill tone="outline" size="sm">
              Overview fetched in {overview.ms} ms
            </Pill>
            <Pill tone="outline" size="sm">
              {Object.keys(overview.lexicon || {}).length} lexic entries
            </Pill>
          </Stack>
        </>
      )}
    </Box>
  );
}
